import { supabase } from '../lib/supabase';

/**
 * Service for managing exercise completion and evidence submission
 */

/**
 * Upload a file to Supabase Storage for exercise evidence
 * @param {File} file - The file to upload
 * @param {string} exerciseId - The exercise ID
 * @param {string} userId - The user ID
 * @returns {Promise<{success: boolean, url?: string, error?: string}>}
 */
export async function uploadExerciseEvidence(file, exerciseId, userId) {
  try {
    // Debug logging
    console.log('=== FILE UPLOAD DEBUG ===');
    console.log('File:', file.name, 'Size:', file.size, 'Type:', file.type);
    console.log('Exercise ID:', exerciseId);
    console.log('User ID:', userId);
    console.log('Auth user:', await supabase.auth.getUser());
    
    // Validate file
    if (!file) {
      throw new Error('No file provided');
    }

    // Check file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      throw new Error('File size must be less than 10MB');
    }

    // Check file type
    const allowedTypes = [
      'image/jpeg',
      'image/png', 
      'image/gif',
      'image/webp',
      'application/pdf',
      'text/plain',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    if (!allowedTypes.includes(file.type)) {
      throw new Error('File type not supported. Please upload images, PDFs, or documents.');
    }

    // Create file path: exercise-evidence/{userId}/{timestamp}-{filename}
    // Simplified path structure to avoid RLS issues
    const timestamp = Date.now();
    const fileName = `${timestamp}-${file.name}`;
    const filePath = `exercise-evidence/${userId}/${fileName}`;
    
    console.log('File path:', filePath);
    console.log('========================');

    // Upload to Supabase Storage
    const { data, error } = await supabase.storage
      .from('CareerLift_Bucket')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) {
      console.error('Upload error details:', error);
      
      // Try alternative path structure if first attempt fails
      if (error.message.includes('row-level security')) {
        console.log('RLS error detected, trying alternative path...');
        const altPath = `uploads/${fileName}`;
        
        const { data: altData, error: altError } = await supabase.storage
          .from('CareerLift_Bucket')
          .upload(altPath, file, {
            cacheControl: '3600',
            upsert: false
          });
          
        if (altError) {
          console.error('Alternative upload also failed:', altError);
          throw new Error(`Upload failed: ${error.message}. Alternative attempt also failed: ${altError.message}`);
        }
        
        console.log('Alternative upload successful:', altData);
        
        // Get public URL for alternative path
        const { data: altUrlData } = supabase.storage
          .from('CareerLift_Bucket')
          .getPublicUrl(altPath);
          
        return {
          success: true,
          url: altUrlData.publicUrl,
          path: altPath,
          fileName: file.name,
          fileSize: file.size,
          mimeType: file.type
        };
      }
      
      throw new Error(`Upload failed: ${error.message}`);
    }

    console.log('Upload successful:', data);

    // Get public URL
    const { data: urlData } = supabase.storage
      .from('CareerLift_Bucket')
      .getPublicUrl(filePath);

    return {
      success: true,
      url: urlData.publicUrl,
      path: filePath,
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type
    };

  } catch (error) {
    console.error('Error uploading exercise evidence:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Save exercise evidence to database
 * @param {Object} evidenceData - Evidence data
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export async function saveExerciseEvidence(evidenceData) {
  try {
    const { data, error } = await supabase
      .from('exercise_evidence')
      .insert([evidenceData])
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to save evidence: ${error.message}`);
    }

    return {
      success: true,
      data
    };

  } catch (error) {
    console.error('Error saving exercise evidence:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Complete an exercise with evidence and reflection
 * @param {string} exerciseId - The exercise ID
 * @param {Object} completionData - Completion data including evidence and reflection
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export async function completeExercise(exerciseId, completionData) {
  try {
    const { evidence, reflection, progress, userId } = completionData;

    // For mock exercises, we need to create a new record in user_exercises
    // since the exerciseId is just a mock ID (like "3")
    
    // First, check if this exercise already exists in user_exercises
    const { data: existingExercise, error: fetchError } = await supabase
      .from('user_exercises')
      .select('*')
      .eq('exercise_id', exerciseId)
      .eq('user_id', userId)
      .single();

    let exerciseUpdate;

    if (existingExercise) {
      // Update existing record
      exerciseUpdate = supabase
        .from('user_exercises')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString(),
          reflection: reflection || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', existingExercise.id)
        .select()
        .single();
    } else {
      // Create new record for mock exercise
      exerciseUpdate = supabase
        .from('user_exercises')
        .insert({
          user_id: userId,
          exercise_id: exerciseId,
          section_id: 'mock_section',
          plan_id: 'mock_plan',
          status: 'completed',
          completed_at: new Date().toISOString(),
          reflection: reflection || null,
          evidence_files: evidence ? JSON.stringify(evidence) : '[]'
        })
        .select()
        .single();
    }

    // Execute the exercise update
    const result = await exerciseUpdate;
    
    if (result.error) {
      throw new Error(`Database update failed: ${result.error.message}`);
    }

    return {
      success: true,
      data: result.data // Return the updated/created exercise
    };

  } catch (error) {
    console.error('Error completing exercise:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Get exercise evidence for a specific exercise
 * @param {string} exerciseId - The exercise ID
 * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
 */
export async function getExerciseEvidence(exerciseId) {
  try {
    const { data, error } = await supabase
      .from('exercise_evidence')
      .select('*')
      .eq('exercise_id', exerciseId)
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(`Failed to fetch evidence: ${error.message}`);
    }

    return {
      success: true,
      data: data || []
    };

  } catch (error) {
    console.error('Error fetching exercise evidence:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Get user's exercises with their status
 * @param {string} userId - The user ID
 * @param {Object} filters - Optional filters
 * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
 */
export async function getUserExercises(userId, filters = {}) {
  try {
    let query = supabase
      .from('user_exercises')
      .select(`
        *,
        exercise_evidence (*),
        exercise_progress (*)
      `)
      .eq('user_id', userId);

    // Apply filters
    if (filters.status) {
      query = query.eq('status', filters.status);
    }

    if (filters.planId) {
      query = query.eq('plan_id', filters.planId);
    }

    if (filters.dueDate) {
      query = query.lte('due_date', filters.dueDate);
    }

    const { data, error } = await query.order('due_date', { ascending: true });

    if (error) {
      throw new Error(`Failed to fetch exercises: ${error.message}`);
    }

    return {
      success: true,
      data: data || []
    };

  } catch (error) {
    console.error('Error fetching user exercises:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Update exercise progress for a specific step
 * @param {string} exerciseId - The exercise ID
 * @param {number} stepNumber - The step number
 * @param {Object} progressData - Progress data
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export async function updateExerciseProgress(exerciseId, stepNumber, progressData) {
  try {
    const { data, error } = await supabase
      .from('exercise_progress')
      .upsert({
        exercise_id: exerciseId,
        user_id: progressData.userId,
        step_number: stepNumber,
        step_title: progressData.stepTitle,
        is_completed: progressData.isCompleted,
        completed_at: progressData.isCompleted ? new Date().toISOString() : null,
        notes: progressData.notes,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'exercise_id,step_number',
        ignoreDuplicates: false
      })
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to update progress: ${error.message}`);
    }

    return {
      success: true,
      data
    };

  } catch (error) {
    console.error('Error updating exercise progress:', error);
    return {
      success: false,
      error: error.message
    };
  }
}
